import { Entities } from '@/entities/api/entities'
import { ProductEntity } from '@/entities/api/products/product'
import React, { useState } from 'react'

interface ProductDetailsProps {
	product?: ProductEntity | null
}

const ProductDetails: React.FC<ProductDetailsProps> = ({ product }) => {
	const [isOpen, setIsOpen] = useState(false)

	if (!product) return null

	const InfoRow: React.FC<{
		label: string
		value?: string
		isBoolean?: boolean
		booleanValue?: boolean
	}> = ({ label, value, isBoolean, booleanValue }) => (
		<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 border-b border-gray-100 last:border-b-0 gap-2 sm:gap-0'>
			<span className='text-gray-700 text-sm sm:text-base font-semibold'>
				{label}
			</span>
			{isBoolean ? (
				<div className='flex items-center gap-2'>
					<div
						className={`w-3 h-3 rounded-full border-2 ${
							booleanValue
								? 'bg-red-500 border-red-500 shadow-sm'
								: 'bg-gray-200 border-gray-300'
						}`}
					/>
					<span
						className={`text-sm sm:text-base font-bold ${
							booleanValue ? 'text-red-600' : 'text-gray-500'
						}`}
					>
						{booleanValue ? 'Sí' : 'No'}
					</span>
				</div>
			) : (
				<span className='text-gray-900 text-sm sm:text-base font-bold max-w-full sm:max-w-48 text-left sm:text-right break-words'>
					{value || 'No especificado'}
				</span>
			)}
		</div>
	)

	const renderDetails = () => {
		switch (product.type) {
			case Entities.SUPPLEMENTS:
				const suppDetails = product.details as any
				return (
					<>
						<InfoRow label='Tipo' value={suppDetails.general_details?.type} />
						<InfoRow label='Marca' value={suppDetails.general_details?.brand} />
						<InfoRow
							label='Suplemento principal'
							value={suppDetails.general_details?.principal_supplement}
						/>
						<InfoRow
							label='Formato'
							value={suppDetails.buy_format?.suplement_format}
						/>
						<InfoRow
							label='Contenedor'
							value={suppDetails.buy_format?.container_type}
						/>
						<InfoRow
							label='Sin gluten'
							isBoolean
							booleanValue={suppDetails.specifications?.is_gluten_free}
						/>
						<InfoRow
							label='Vegano'
							isBoolean
							booleanValue={suppDetails.specifications?.is_vegan}
						/>
						<InfoRow
							label='Contiene lactosa'
							isBoolean
							booleanValue={suppDetails.specifications?.have_lactose}
						/>
						<InfoRow
							label='Ingredientes'
							value={suppDetails.nutritional_information?.ingredients}
						/>
						<InfoRow
							label='Valor nutricional por porción'
							value={
								`${suppDetails.nutritional_information?.nutritional_portion_value} Gramos`
							}
						/>
						<InfoRow
							label='Peso por porción'
							value={`${suppDetails.nutritional_information?.portion_weight} Gramos`}
						/>
					</>
				)

			case Entities.CLOTHES:
				const clothesDetails = product.details as any
				return (
					<>
						<InfoRow
							label='Modelo'
							value={clothesDetails.general_details?.model}
						/>
						<InfoRow
							label='Marca'
							value={clothesDetails.general_details?.brand}
						/>
						<InfoRow
							label='Género'
							value={clothesDetails.general_details?.gender}
						/>
						<InfoRow label='Edad' value={clothesDetails.general_details?.age} />
						<InfoRow
							label='Materiales'
							value={clothesDetails.general_details?.materials}
						/>
					</>
				)

			case Entities.ACCESSORIES:
				const accessDetails = product.details as any
				return (
					<>
						<InfoRow
							label='Modelo'
							value={accessDetails.general_details?.model}
						/>
						<InfoRow
							label='Marca'
							value={accessDetails.general_details?.brand}
						/>
						<InfoRow label='Edad' value={accessDetails.general_details?.age} />
						<InfoRow
							label='Género'
							value={accessDetails.general_details?.gender}
						/>
						<InfoRow
							label='Antideslizante'
							isBoolean
							booleanValue={accessDetails.specifications?.is_non_slip}
						/>
						<InfoRow
							label='Ajustable'
							isBoolean
							booleanValue={accessDetails.specifications?.is_adjustable}
						/>
						<InfoRow
							label='Materiales'
							value={accessDetails.general_details?.materials}
						/>
					</>
				)

			case Entities.PLANS_PERSONALIZED:
				const plansDetails = product.details as any
				return (
					<>
						<InfoRow
							label='Duración'
							value={`${plansDetails.general_details?.duration} Dias`}
						/>
						<InfoRow
							label='Modalidad'
							value={plansDetails.general_details?.modality}
						/>
						<InfoRow
							isBoolean
							label='Plan nutricional'
							booleanValue={plansDetails.general_details?.nutrition_plan}
						/>
						<InfoRow
							isBoolean
							label='Plan de entrenamiento'
							booleanValue={plansDetails.general_details?.training_plan}
						/>
						<InfoRow
							label='Lugar'
							value={plansDetails.general_details?.place}
						/>
					</>
				)

			default:
				return null
		}
	}

	return (
		<div className='bg-white border-2 mt-6 mb-6 lg:mt-8 lg:mb-8  border-gray-200 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden'>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className='w-full px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 flex items-center justify-between transition-all duration-300 group'
			>
				<div className='flex items-center gap-3'>
					<div className='w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center'>
						<svg
							className='w-4 h-4 text-white'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
							/>
						</svg>
					</div>
					<h3 className='text-base sm:text-lg font-bold text-white'>
						Especificaciones del Producto
					</h3>
				</div>
				<div className='flex items-center gap-2'>
					<span className='hidden sm:block text-white text-sm font-medium opacity-90'>
						{isOpen ? 'Ocultar' : 'Ver detalles'}
					</span>
					<svg
						className={`w-6 h-6 text-white transform transition-all duration-300 group-hover:scale-110 ${
							isOpen ? 'rotate-180' : ''
						}`}
						fill='none'
						stroke='currentColor'
						viewBox='0 0 24 24'
					>
						<path
							strokeLinecap='round'
							strokeLinejoin='round'
							strokeWidth={2.5}
							d='M19 9l-7 7-7-7'
						/>
					</svg>
				</div>
			</button>
			<div
				className={`overflow-hidden transition-all duration-500 ease-in-out ${
					isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
				}`}
			>
				<div className='px-4 sm:px-6 py-6 '>
					<div className='bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-100'>
						{renderDetails()}
					</div>
				</div>
			</div>
		</div>
	)
}

export default ProductDetails
